import { Loader2, Upload, Copy, Scale, DollarSign, CheckCircle, XCircle, Users, ShoppingCart } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { PhotoUploadField } from '../PhotoUploadField';
import { api } from '../../lib/api';

const uploadArtistImage = async (fd) => (await api.adminUploadArtistImage(fd)).data.image_url;

export function UserDetailsDialog({ open, onOpenChange, user }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-[#262626] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>User Details</DialogTitle></DialogHeader>
        {user && (
          <div className="space-y-6">
            <div className="bg-surface-highlight rounded-lg p-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-lime/20 rounded-full flex items-center justify-center"><Users className="w-8 h-8 text-lime" /></div>
                <div>
                  <h3 className="text-xl font-bold">{user.name}</h3>
                  <p className="text-[#A1A1AA]">{user.email}</p>
                  <Badge className={`mt-1 capitalize ${user.role === 'admin' ? 'bg-red-500/20 text-red-400' : user.role === 'artist' ? 'bg-lime/20 text-lime' : 'bg-blue-500/20 text-blue-400'}`}>{user.role}</Badge>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-surface-highlight rounded-lg p-4 text-center"><p className="text-2xl font-mono font-bold">{user.total_orders || 0}</p><p className="text-sm text-[#A1A1AA]">Total Orders</p></div>
              <div className="bg-surface-highlight rounded-lg p-4 text-center"><p className="text-2xl font-mono font-bold text-lime">${(user.total_spent || 0).toFixed(2)}</p><p className="text-sm text-[#A1A1AA]">Total Spent</p></div>
              <div className="bg-surface-highlight rounded-lg p-4 text-center"><p className="text-2xl font-mono font-bold">{user.completed_orders || 0}</p><p className="text-sm text-[#A1A1AA]">Completed</p></div>
            </div>
            <div>
              <h4 className="font-heading font-bold mb-3 flex items-center gap-2"><ShoppingCart className="w-4 h-4" />Order History</h4>
              {user.orders && user.orders.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {user.orders.map((order) => (
                    <div key={order.id} className="bg-surface-highlight rounded-lg p-3 flex items-center justify-between">
                      <div><p className="font-medium">{order.artist_name}</p><p className="text-xs text-[#666]">{new Date(order.created_at).toLocaleDateString()}</p></div>
                      <div className="text-right">
                        <p className="font-mono">${order.price?.toFixed(2)}</p>
                        <Badge className={`capitalize text-xs ${order.status === 'completed' ? 'bg-green-500/20 text-green-400' : order.status === 'delivered' ? 'bg-blue-500/20 text-blue-400' : order.status === 'accepted' ? 'bg-yellow-500/20 text-yellow-400' : order.status === 'cancelled' ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20 text-gray-400'}`}>{order.status?.replace('_', ' ')}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#A1A1AA] text-sm">No orders yet</p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ArtistFormDialog({ open, onOpenChange, editingArtist, artistForm, setArtistForm, handleSaveArtist, saving, AVAILABLE_GENRES, handleGenreToggle }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-[#262626] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingArtist ? 'Edit Artist' : 'Add New Artist'}</DialogTitle>
          <DialogDescription>{editingArtist ? 'Update artist information' : 'Fill in the details to add a new artist. They will receive a setup link to complete their profile.'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label htmlFor="name">Name *</Label><Input id="name" value={artistForm.name} onChange={(e) => setArtistForm({ ...artistForm, name: e.target.value })} className="input-dark" data-testid="artist-name-input" /></div>
            <div className="space-y-2"><Label htmlFor="email">Email <span className="text-[#666] text-xs">(optional)</span></Label><Input id="email" type="email" value={artistForm.email} onChange={(e) => setArtistForm({ ...artistForm, email: e.target.value })} className="input-dark" placeholder="artist@email.com" data-testid="artist-email-input" /></div>
          </div>
          <div className="space-y-2"><Label htmlFor="bio">Bio</Label><Textarea id="bio" value={artistForm.bio} onChange={(e) => setArtistForm({ ...artistForm, bio: e.target.value })} className="input-dark min-h-[100px]" data-testid="artist-bio-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label htmlFor="price">Price (USD)</Label><Input id="price" type="number" step="0.01" value={artistForm.price} onChange={(e) => setArtistForm({ ...artistForm, price: e.target.value })} className="input-dark" data-testid="artist-price-input" /></div>
            <div className="space-y-2"><Label htmlFor="delivery_days">Delivery Days</Label><Input id="delivery_days" type="number" value={artistForm.delivery_days} onChange={(e) => setArtistForm({ ...artistForm, delivery_days: e.target.value })} className="input-dark" data-testid="artist-delivery-input" /></div>
          </div>
          <PhotoUploadField
            label="Artist Photo"
            value={artistForm.image_url}
            onChange={(url) => setArtistForm((prev) => ({ ...prev, image_url: url }))}
            uploadFn={uploadArtistImage}
            testIdPrefix="artist-image"
          />
          <div className="space-y-2"><Label htmlFor="spotify_url">Spotify URL</Label><Input id="spotify_url" value={artistForm.spotify_url} onChange={(e) => setArtistForm({ ...artistForm, spotify_url: e.target.value })} className="input-dark" placeholder="https://open.spotify.com/artist/..." data-testid="artist-spotify-input" /></div>
          <div className="space-y-2">
            <Label>Genres</Label>
            <div className="flex flex-wrap gap-2">
              {AVAILABLE_GENRES.map(genre => (
                <button key={genre.value} type="button" onClick={() => handleGenreToggle(genre.value)} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${artistForm.genres.includes(genre.value) ? 'bg-lime text-black border-lime' : 'bg-transparent text-[#A1A1AA] border-[#404040] hover:border-lime'}`} data-testid={`genre-${genre.value}`}>{genre.label}</button>
              ))}
            </div>
            <p className="text-xs text-[#666]">Select one or more genres</p>
          </div>
          {editingArtist && (
            <>
              <div className="pt-4 border-t border-[#262626]">
                <Label className="mb-3 block">Payout Method</Label>
                <Select value={artistForm.payout_method} onValueChange={(value) => setArtistForm({ ...artistForm, payout_method: value })}>
                  <SelectTrigger className="input-dark"><SelectValue placeholder="Select payout method" /></SelectTrigger>
                  <SelectContent className="bg-surface border-[#262626]"><SelectItem value="paypal">PayPal</SelectItem><SelectItem value="stripe">Stripe Connect</SelectItem></SelectContent>
                </Select>
              </div>
              {artistForm.payout_method === 'paypal' && (
                <div className="space-y-2"><Label htmlFor="paypal_email">PayPal Email</Label><Input id="paypal_email" type="email" value={artistForm.paypal_email} onChange={(e) => setArtistForm({ ...artistForm, paypal_email: e.target.value })} className="input-dark" placeholder="artist@paypal.email" data-testid="artist-paypal-input" /></div>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#262626]">Cancel</Button>
          <Button onClick={handleSaveArtist} disabled={saving} className="btn-primary" data-testid="save-artist-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}{editingArtist ? 'Update' : 'Add'} Artist</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LinkArtistDialog({ open, onOpenChange, linkUserEmail, setLinkUserEmail, handleLinkArtist }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-[#262626]">
        <DialogHeader><DialogTitle>Link Artist to User</DialogTitle><DialogDescription>Enter the email of the user account to link with this artist profile</DialogDescription></DialogHeader>
        <div className="space-y-4"><div className="space-y-2"><Label htmlFor="link_email">User Email</Label><Input id="link_email" type="email" value={linkUserEmail} onChange={(e) => setLinkUserEmail(e.target.value)} className="input-dark" placeholder="artist@example.com" data-testid="link-email-input" /></div></div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#262626]">Cancel</Button>
          <Button onClick={handleLinkArtist} className="btn-primary" data-testid="link-artist-btn">Link Artist</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AudioUploadDialog({ open, onOpenChange, audioInputRef, uploadingAudio, handleUploadAudioSample }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-[#262626]">
        <DialogHeader><DialogTitle>Upload Audio Sample</DialogTitle><DialogDescription>Upload an audio preview for this artist's profile</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="border-2 border-dashed border-[#262626] rounded-xl p-8 text-center hover:border-lime transition-colors">
            <input ref={audioInputRef} type="file" accept=".mp3,.wav,.m4a,.aac,audio/*" onChange={handleUploadAudioSample} className="hidden" id="audio-upload" data-testid="audio-upload-input" />
            <label htmlFor="audio-upload" className="cursor-pointer">
              {uploadingAudio ? (
                <div className="flex flex-col items-center"><Loader2 className="w-8 h-8 animate-spin text-lime mb-2" /><span className="text-sm">Uploading...</span></div>
              ) : (
                <div className="flex flex-col items-center"><Upload className="w-8 h-8 text-[#666] mb-2" /><span className="text-sm text-[#A1A1AA]">Click to upload audio sample</span><span className="text-xs text-[#666] mt-1">MP3, WAV, M4A, AAC</span></div>
              )}
            </label>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SetupLinkDialog({ open, onOpenChange, setupLink, copyToClipboard }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-[#262626]">
        <DialogHeader><DialogTitle>Artist Setup Link</DialogTitle><DialogDescription>Share this link with the artist to complete their profile setup</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="p-4 bg-surface-highlight rounded-lg">
            <p className="text-sm text-[#A1A1AA] mb-2">Setup Link (one-time use):</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm bg-[#050505] p-3 rounded-lg break-all text-lime">{setupLink}</code>
              <Button size="icon" variant="ghost" onClick={() => copyToClipboard(setupLink)} className="h-10 w-10"><Copy className="w-4 h-4" /></Button>
            </div>
          </div>
          <p className="text-sm text-[#A1A1AA]">The artist will use this link to set their password and complete their profile. This link can only be used once.</p>
        </div>
        <DialogFooter><Button onClick={() => onOpenChange(false)} className="btn-primary">Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ResolveDisputeDialog({ open, onOpenChange, selectedDispute, resolutionType, setResolutionType, refundAmount, setRefundAmount, resolutionNotes, setResolutionNotes, resolvingDispute, handleResolveDispute }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-[#262626] max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Scale className="w-5 h-5 text-lime" />Resolve Dispute</DialogTitle>
          <DialogDescription>Choose how to resolve this dispute for order #{selectedDispute?.order_id?.slice(0, 8)}</DialogDescription>
        </DialogHeader>
        {selectedDispute && (
          <div className="space-y-4 py-4">
            <div className="bg-[#1a1a1a] rounded-lg p-3 text-sm">
              <div className="flex justify-between mb-1"><span className="text-[#888]">Issue</span><span className="font-medium">{selectedDispute.reason_label}</span></div>
              <div className="flex justify-between mb-1"><span className="text-[#888]">Order Amount</span><span className="font-mono">${selectedDispute.order_details?.price?.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-[#888]">Raised By</span><span>{selectedDispute.raised_by_name} ({selectedDispute.raised_by_role})</span></div>
            </div>
            <div>
              <Label>Resolution Type</Label>
              <Select value={resolutionType} onValueChange={setResolutionType}>
                <SelectTrigger className="input-dark mt-1" data-testid="resolution-type-select"><SelectValue placeholder="Select resolution..." /></SelectTrigger>
                <SelectContent className="bg-surface border-[#262626]">
                  <SelectItem value="full_refund" className="focus:bg-surface-highlight"><div className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-red-400" />Full Refund to Buyer</div></SelectItem>
                  <SelectItem value="partial_refund" className="focus:bg-surface-highlight"><div className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-yellow-400" />Partial Refund</div></SelectItem>
                  <SelectItem value="release_to_artist" className="focus:bg-surface-highlight"><div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-lime" />Release Funds to Artist</div></SelectItem>
                  <SelectItem value="no_action" className="focus:bg-surface-highlight"><div className="flex items-center gap-2"><XCircle className="w-4 h-4 text-gray-400" />Close - No Action</div></SelectItem>
                </SelectContent>
              </Select>
            </div>
            {resolutionType === 'partial_refund' && (
              <div>
                <Label>Refund Amount (USD)</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                  <Input type="number" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} className="input-dark pl-10" placeholder="0.00" min="0.01" max={selectedDispute.order_details?.price} step="0.01" data-testid="refund-amount-input" />
                </div>
                <p className="text-xs text-[#888] mt-1">Max: ${selectedDispute.order_details?.price?.toFixed(2)}</p>
              </div>
            )}
            <div>
              <Label>Resolution Notes</Label>
              <Textarea value={resolutionNotes} onChange={(e) => setResolutionNotes(e.target.value)} placeholder="Explain the reason for this resolution..." className="input-dark mt-1 min-h-[100px]" data-testid="resolution-notes" />
              <p className="text-xs text-[#888] mt-1">This will be shared with both parties</p>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={resolvingDispute}>Cancel</Button>
          <Button onClick={handleResolveDispute} disabled={resolvingDispute || !resolutionType || !resolutionNotes.trim()} className="btn-primary" data-testid="confirm-resolution-btn">
            {resolvingDispute ? (<><Loader2 className="w-4 h-4 animate-spin mr-2" />Resolving...</>) : (<><CheckCircle className="w-4 h-4 mr-2" />Confirm Resolution</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
